import CSS from 'csstype';
import jsPDF from 'jspdf';
import moment from 'moment-timezone';
import store from "app/main/store";
import html2canvas from 'html2canvas';
import { IBooking } from 'app/app.types';
import { VerticalBarSeriesPoint } from "react-vis";
import { saveSelectedReport, setIsLoading, setIsDirty } from 'app/reducers/dashboardSlice';
import { ReportingService } from 'app/services/reporting/reporting.service';
import { IBarGraphProps, IFilterParameters, IBookingDetails, IReport } from "./types";
import { IAverageSpendDay, IAverageSpendMonth, IAverageSpendPerPax, IPurchasedItem } from "app/services/reporting/reporting.type";
import { StoreAvgMonthlySpendStats, 
  StoreAvgPerPaxSpendStats, 
  StoreAvgPerSpendDurationStats, 
  StoreAvgWeeklySpendStats, 
  StoreBookingsByTheDate, 
  StoreBookingsInfoByFilters, 
  StoreSchedulesByTheDate, 
  StoreServicesByTheDate, 
  StoreTotalMonthlySpendStats } from './service';

type IAverageSpendType = IAverageSpendDay | IAverageSpendMonth;

/**
 * List of div ids which are present on the dashboard page.
 */
export const DashboardDivIdList = ['todaysGuestTable', 'avgSpendWrapper', 'durationTable'];

/** Contains the various reports displayed on the dashboard */
export enum DashboardPage {
  REPORTING_DETAILS = 0,
  BOOKING_DETAILS = 1,
  AMOUNT_SPENT_DETAILS = 2,
  SERVICES = 3
}

/** Css styles for Hint */
export const CssStylesForHint: React.CSSProperties = {
  margin: '0px',
  padding: '0px',
  background: 'black',
  borderRadius: '3px'
}

/** Contains the product categories displayed on the purchased item table */
export const ProductCategories = ['Show all', 'Food', 'Beverage'];

/** Contains the report list */
export const ReportingOptions: IReport[] = [
  {
    id: 0,
    name: "Reporting Dashboard",
    urlParamText: "report"
  },
  {
    id: 1,
    name: "Booking Details",
    urlParamText: "guests"
  },
  {
    id: 2,
    name: "Amount Spent Details",
    urlParamText: "amount"
  },
  {
    id: 3,
    name: 'Services',
    urlParamText: "services"
  }
];

/** Initial state of the bar graph */
export const InitialBarGraphState: IBarGraphProps = {
  title: "",
  barSeriesPointsGroup: null
}

/** Fetch the dashboard statistics by calling the backend APIs */
export async function GetDashboardStatistics(activePage: IReport, filterParameters: IFilterParameters): Promise<void> {
  try
  {
    store.dispatch(setIsLoading(true));
    if (activePage.id === DashboardPage.REPORTING_DETAILS) {
      await StoreAvgPerPaxSpendStats(filterParameters);
      await StoreAvgPerSpendDurationStats(filterParameters);
      await StoreBookingsInfoByFilters(filterParameters);
    } else if (activePage.id === DashboardPage.BOOKING_DETAILS) {
      await StoreBookingsInfoByFilters(filterParameters);
    } else if (activePage.id === DashboardPage.AMOUNT_SPENT_DETAILS) {
      await StoreAvgWeeklySpendStats(filterParameters);
      await StoreAvgMonthlySpendStats(filterParameters);
      await StoreTotalMonthlySpendStats(filterParameters);
    } else if (activePage.id == DashboardPage.SERVICES) {
      await StoreBookingsByTheDate(filterParameters);
      await StoreServicesByTheDate(filterParameters);
      await StoreSchedulesByTheDate(filterParameters);
    }
  }
  catch {
    console.warn('GetDashboardStatistics: Error Loading Data.');
  } 
  finally {
    store.dispatch(setIsLoading(false));
    store.dispatch(setIsDirty(false));
  }
}

/** Get product list for the specified customer */
export async function GetPurchasedItems(venueId: number, customerId: string): Promise<IPurchasedItem[]> {

  const result: IPurchasedItem[] = [];
  if (!customerId) return result;

  return await ReportingService.getItemsByCustomerId(venueId, customerId);
}

/** Save the selected report in store */
export function SelectReportByUrlParameter(paramValue: string): void {
  const selectedReport = ReportingOptions.find(option => option.urlParamText === paramValue) || ReportingOptions[0];
  store.dispatch(saveSelectedReport(selectedReport));
}

export function MakeBookingDetails(booking: IBooking): IBookingDetails {

  // Prefix lastName with ','
  const lastName = (booking.customer?.lastName ?? booking.lastName) ? ` ${(booking.customer?.lastName ?? booking.lastName)}` : '';

  /**
   * Iterate through tags, and then convert to comma separated list
   * Convert to string because only string/number value is required for sorting the table
   */
  const tagList = booking.tags.map(item => item.name).toString() || "";

  const bookingInfo: IBookingDetails = {
    tags: tagList,
    customerVisitCount: booking.visits,
    fullName: `${(booking.customer?.firstName ?? booking.firstName)}${lastName}`,
    firstName: booking.customer?.firstName ?? booking.firstName,
    lastName: booking.customer?.lastName ?? booking.lastName,
    companyName: booking.customer?.company ?? booking.company,
    venueName: booking.venueName,
    bookingStatus: booking.status ? booking.status.name : "",
    bookingDuration: booking.duration,
    bookingTime: booking.time,
    tableList: booking.tableList,
    customerId: booking.customer?._id ?? booking.customerId,
    averageAmountInDollar: ConvertAmountToDollar(booking.customer?.averageSpend),
    totalAmountInDollar: ConvertAmountToDollar(booking.customer?.totalSpend),
    averageAmountSpentOnFoodInDollar: ConvertAmountToDollar(booking.customer?.averageFoodSpend),
    averageAmountSpentOnBeverageInDollar: ConvertAmountToDollar(booking.customer?.averageBeverageSpend)
  };

  return bookingInfo;
}

export function MakeBarGraphProps(title: string, apiResult: IAverageSpendType[]): IBarGraphProps {

  // Validate service
  const hasValidData = apiResult.some(item => item.service.length > 0);
  if (!hasValidData) return InitialBarGraphState;

  const barSeriesPoints: VerticalBarSeriesPoint[] = [];

  // Iterate through each row
  apiResult.forEach((item: IAverageSpendType) => {

    const seriesPointList = item.service.map(service => {
      const xValue = (<IAverageSpendDay>item).day || (<IAverageSpendMonth>item).month;
      const seriesPoint: VerticalBarSeriesPoint = {
        x: xValue,
        y: service.spendInDollar,
        header: xValue,
        amount: service.spendInDollar,
        service: service.serviceName
      }
      return seriesPoint;
    });

    barSeriesPoints.push(...seriesPointList);
  });

  return MakeBarSeriesPointsGroup(barSeriesPoints, title);
}

export function MakeAvgSpendBarGraphProps(apiResult: IAverageSpendPerPax[]): IBarGraphProps {

  // Validate input
  if (!apiResult || apiResult.length === 0) return InitialBarGraphState;

  const barSeriesPoints: VerticalBarSeriesPoint[] = [];

  // Sort the list as per booked people in ascending order
  const sortedList = apiResult.slice().sort((x,y) => x.bookedPeople > y.bookedPeople ? 1 : -1);

  // Iterate through each row
  sortedList.forEach((item: IAverageSpendPerPax) => {

    const seriesPoint: VerticalBarSeriesPoint = {
      service: "Amount",
      x: item.bookedPeople,
      color: item.HexColorCode,
      y: item.AverageSpendInDollar,
      header: `People: ${item.bookedPeople}`,
      amount: item.AverageSpendInDollar?.toFixed(2)
    }

    barSeriesPoints.push(seriesPoint);
  });

  return MakeBarSeriesPointsGroup(barSeriesPoints);

}

function MakeBarSeriesPointsGroup(barSeriesPoints: VerticalBarSeriesPoint[], title?: string) {

  // Create the list of key-value pair
  const barSeriesPointsGroup: Record<string, VerticalBarSeriesPoint[]> = barSeriesPoints.reduce((list, item) => {

    // Creates a key with the service name i.e. Breakfast, Lunch, Dinner, etc.
    list[item.service] = list[item.service] || [];

    // Add the series point as value
    list[item.service].push(item);

    return list;
  }, {} as VerticalBarSeriesPoint);

  const barGraphProps: IBarGraphProps = { title, barSeriesPointsGroup }
  return barGraphProps;
}

/**
 * Check whether searched input exists or not in the booking details.
 */
export function IsSearchedTextFound(booking: IBookingDetails, searchText: string): boolean {

  const lowerCaseSearchText = searchText?.toLowerCase();

  // Search firstName
  if (booking.firstName?.toLowerCase().includes(lowerCaseSearchText)) return true;

  // Search lastName
  if (booking.lastName?.toLowerCase().includes(lowerCaseSearchText)) return true;

  // Search tags
  if (booking.tags?.toLowerCase().includes(lowerCaseSearchText)) return true;

  return false;
}

/**
 * Converts the html div contents into an image and then exports in pdf format.
 */
export async function GeneratePDF(fileName: string, divIdList: string[]) {

  let pageNumber = 0;
  const pdf = new jsPDF({compress: true});

  // Iterate through the each div element inorder to export
  for (let index = 0; index < divIdList.length; index++) {

    const divToExport = document.getElementById(divIdList[index]);

    // Add div element to the pdf file if exists
    if (divToExport) {

      pageNumber++;

      // Do not add a page to the pdf file since, it's added by default for the first element
      if (pageNumber > 1) pdf.addPage();

      // Converts html content to canvas image
      const canvas = await html2canvas(divToExport, { scale: 2 });

      // Add image to pdf file
      pdf.addImage(canvas, 'PNG', 10, 30, 180, 180);
    }
  }

  // Export pdf, when atleast one div element exist
  if (pageNumber > 0) pdf.save(`${moment().format('YYYY-MM-DD')}-${fileName}.pdf`);
}

/** Generates random hex color code */
export function GenerateRandomHexColorCode(): string {

  // Refer to this link: https://www.paulirish.com/2009/random-hex-color-code-snippets/
  const color = '#' + Math.floor(Math.random()*16777215).toString(16).padStart(6, '0');
  return color;
}

/**
 * Format the amount upto 2 decimal places.
 */
export function ConvertAmountToDollar(amount: number): number {
  return (!isNaN(amount) && amount > 0) ? amount/100 : 0;
}

/**
 * Prefix amount with "$" symbol.
 */
export function AppendDollarToAmount(amount: number): string {
  return !!amount ? `$${amount.toFixed(2)}` : null;
}

export function getRandomColorForGraph (isMultipleVenuesSelected: boolean): string {
  if (isMultipleVenuesSelected) {
    return '#'+Math.floor(Math.random()*16777215).toString(16);
  }
  return '#FFA500';
}

export const getUtcDateByVenueTimeZone = (venueTimeZoneId: string, theDate: string) => { 
  if (!theDate || theDate.length === 0) return theDate;
  
  if (!venueTimeZoneId || venueTimeZoneId.length === 0) 
    return moment(theDate, "YYYY-MM-DD").toISOString();

    const m = moment.tz(theDate, "YYYY-MM-DD", venueTimeZoneId);
    return m.utc().toISOString();
}

export const getVenuetDateTimeFromUtc = (venueTimeZoneId: string, theDate: string) => {
    if (!theDate || theDate.length === 0) return theDate;

    if (!venueTimeZoneId || venueTimeZoneId.length === 0)
        return moment(theDate, "YYYY-MM-DD HH:mm:ss").toISOString();

    const m = moment.utc(theDate, "YYYY-MM-DD HH:mm:ss").tz(venueTimeZoneId);
    return m.toISOString();
}